import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import { DisplayModes } from 'services/webedi/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { EMPTY_FUNCTION } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { DocumentType, ProcessSpecificationDTO, RelationshipV2 } from 'support/types';
import { ColumnTypesInternal, WebEDIRowData } from 'support/types/webediTable';
import { withDataTableProvider } from 'components/DataTable';
import { DataTableProvider } from 'components/DataTable/contexts/DataTableContext';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';
import { useGenerateLineItemsColumns } from 'components/WebEDI/useGenerateLineItemsColumns';

import { useDownloadTemplate } from './hooks/useDownloadTemplate';
import { PriceCatalogXLSXUploader } from './components/PriceCatalogXLSXUploader/PriceCatalogXLSXUploader';
import { TemplateDownloader } from './components/TemplateDownloader/TemplateDownloader';
import { markConditionalTemplateColumns } from './helpers';

export type PriceCatalogUploadModalProps = {
  relationship: RelationshipV2;
  processSpec: ProcessSpecificationDTO | undefined;
  onLoad: (data: Array<Record<string, unknown>>, filename: string) => void;
  open: boolean;
  onClose?: () => void;
  onCancel?: () => void;
};

const DATA_PREFIX = 'line_items';
const CATALOG_LINE_IDENTIFIER = 'catalog_line_identifier';
const ITEM_TYPE = 'item.type';

const NON_TEMPLATE_COLUMNS = [CATALOG_LINE_IDENTIFIER, ITEM_TYPE];

const PriceCatalogUploadModalComponent: FC<PriceCatalogUploadModalProps> = ({
  relationship,
  onLoad,
  open,
  processSpec,
  onClose,
  onCancel,
}) => {
  const { t } = useTranslation();
  const uiConfig = useWebEDIStore((state) => state.uiConfig);
  const lineItemsSection = uiConfig.find((section) => section.type === 'line_items');
  const { downloadTemplate, downloadingTemplate } = useDownloadTemplate();

  const { columns } = useGenerateLineItemsColumns({
    config: lineItemsSection?.fields || [],
    mode: DisplayModes.edit,
    documentType: DocumentType.productCatalog,
    numberOfPinnedColumns: 2,
  });

  if (!lineItemsSection) {
    return null;
  }

  // Filter columns to ensure they have a valid id and header
  // For embedded table columns, keep the structure intact and validate sub-columns
  const filteredColumns = columns.filter((column): column is ColumnDef<WebEDIRowData, unknown> & { id: string } => {
    if (!column.id) {
      return false;
    }
    if (ColumnTypesInternal[column.id as keyof typeof ColumnTypesInternal]) {
      return false;
    }
    if (NON_TEMPLATE_COLUMNS.includes(column.id)) {
      return false;
    }

    // For embedded table columns, check if it has valid sub-columns
    if ('columns' in column && Array.isArray(column.columns)) {
      return column.columns.every((subCol) => subCol.meta?.type !== undefined);
    }

    // For regular columns, check if it has a type
    return column.meta?.type !== undefined;
  });

  // The resolved column meta can never say 'conditional' (see helpers.ts) — restore it from the
  // raw process spec so the downloaded template can flag those fields, independent of this document.
  const templateColumns = markConditionalTemplateColumns(filteredColumns, processSpec?.fields);

  const handleDownloadTemplate = () => {
    downloadTemplate(templateColumns, relationship.receiver_partner.name, processSpec?.updated_at);
  };

  return (
    <BasePopup open={open} setOpen={onClose ? onClose : EMPTY_FUNCTION} className="flex max-h-[90vh] flex-col">
      <BasePopup.Header title={t('priceCatalogs:uploadModal.title')} />
      <BasePopup.Body className="flex-1 overflow-y-auto">
        <DataTableProvider name={DATA_PREFIX}>
          <div className="divide-y divide-gray-200 *:not-last:pb-6">
            <section className="flex flex-col gap-3">
              <div>
                <h6 className="text-sm font-medium text-black">
                  1. {t('priceCatalogs:uploadModal.downloadTemplate.title')}
                </h6>
                {processSpec?.updated_at ? (
                  <p className="mt-0.5 ml-[13px] text-xs text-gray-500">
                    {t('priceCatalogs:template.updateAt', { date: formatDayAndTime(processSpec.updated_at) })}
                  </p>
                ) : null}
              </div>
              <div className="ml-[13px]">
                <TemplateDownloader
                  columns={templateColumns}
                  partnerName={relationship.receiver_partner.name}
                  updatedAt={processSpec?.updated_at}
                  onDownload={handleDownloadTemplate}
                  isDownloading={downloadingTemplate}
                />
              </div>
            </section>
            <section className="flex flex-col gap-2 pt-6">
              <h6 className="text-sm font-medium text-black">2. {t('priceCatalogs:uploadModal.instructions.title')}</h6>
              <p className="ml-[13px] text-sm text-gray-500">
                <Translate i18nKey="priceCatalogs:uploadModal.instructions.description" />
              </p>
            </section>

            <section className="flex flex-col gap-3 pt-6">
              <h6 className="text-sm font-medium text-black">
                3. {t('priceCatalogs:uploadModal.uploadTemplate.title')}
              </h6>
              <div className="px-[13px]">
                <PriceCatalogXLSXUploader
                  relationshipId={relationship.id}
                  receiverPartner={relationship.receiver_partner}
                  onDownloadTemplate={handleDownloadTemplate}
                  columns={templateColumns}
                  onLoad={(data, filename) => {
                    onLoad(
                      data.map((data, index) => {
                        return {
                          ...data,
                          [CATALOG_LINE_IDENTIFIER]: index + 1,
                          item: {
                            ...((data.item as object) || {}),
                            type: 'PRODUCT',
                          },
                        };
                      }),
                      filename,
                    );
                  }}
                />
              </div>
            </section>
          </div>
        </DataTableProvider>
      </BasePopup.Body>
      <BasePopup.Footer
        title={t('priceCatalogs:uploadModal.footer.title')}
        action={
          onClose || onCancel ? (
            <Button
              onClick={onClose || onCancel}
              analyticsId={'webedi:price_catalog_upload_modal_close'}
              variant="secondary"
            >
              {t('common:cancel')}
            </Button>
          ) : undefined
        }
      >
        {/* using Translate since it already contains anchor, bolds, etc and we will need it here */}
        <Translate i18nKey="priceCatalogs:uploadModal.footer.description" />
      </BasePopup.Footer>
    </BasePopup>
  );
};

export const PriceCatalogUploadModal = withDataTableProvider(PriceCatalogUploadModalComponent, DATA_PREFIX);
